<template>
  <div
    :class="[
      showBorder ? 'native-input-border' : 'native-input',
      'parent-box row no-wrap',
      { 'is-editable': editOnClick && !disabledField },
      { 'native-input--underline': underlineEdit },
      { 'native-input--empty-warning': isValidationWarning },
      { 'native-input--editing': editingNow },
      { 'native-input--break-anywhere': breakAnywhere }
    ]"
  >
    <div
      class="inner-editable"
      ref="editable_field"
      :contenteditable="isContentEditable"
      @dragstart.prevent
      @keydown.enter="onEnter"
      @blur="onBlur"
      @click="onClick"
      @keydown="handleKeydownUnified"
      @input="onInput"
    ></div>
    <span v-if="isValidationWarning" class="native-input__error-wrap">
      <img :src="errorCircleSvg" class="native-input__error-icon" />
    </span>
    <Teleport to="body">
      <span v-if="isValidationWarning" class="native-input__error-tooltip" :style="tooltipStyle">{{ validationWarningText }}</span>
    </Teleport>
    <span
      v-if="
        showClearIcon ||
        (this.editingNow &&
          realTimeText.length &&
          (inputType == 'text' || realTimeText != '0'))
      "
      class="native-input__clear-wrap"
      @mousedown.prevent
      @click="onClear"
    >
      <q-icon
        name="arrow_back"
        size="18px"
        class="pointer hover-green target-icon native-input__clear-icon"
        style="align-self: baseline; color: #D2D2D2;"
      />
      <q-tooltip :delay="300" anchor="top middle" self="center middle">
        {{ $t('clear_field') }}
      </q-tooltip>
    </span>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from "vue-facing-decorator";
import { buildHighlightSpans } from '@/components/common/basic/search-highlight/searchHighlight';
import errorCircleSvg from '@/assets/error-circle.svg';

@Component({
  name: "NativeInput",
  emits: ["update:modelValue", "keydown", "blur", "update:caretPosition", "liveValue"],
  expose: ["startEdit"],
})
export default class NativeInput extends Vue {
  // v-model
  @Prop({ default: "" })
  modelValue!: string;
  @Prop({ default: undefined })
  maxNumberValue!: number | undefined;
  @Prop({ default: undefined })
  minNumberValue!: number | undefined;
  @Prop({ default: "text" })
  inputType!: "text" | "number";
  @Prop({ default: false })
  editOnClick!: boolean;
  @Prop({ default: false })
  showBorder!: boolean;
  @Prop({ default: false })
  disabledField!: boolean;
  @Prop({ default: true })
  allowLineBreak!: boolean;
  @Prop({ default: true })
  allowEmptyNumber!: boolean;
  @Prop({ default: () => [] })
  highlightTokens!: string[];
  @Prop({ default: false })
  underlineEdit!: boolean;
  @Prop({ default: false })
  externalWarning!: boolean;
  @Prop({ default: '' })
  externalWarningText!: string;
  // Если true — blur и Esc откатывают значение к modelValue без emit'а update:modelValue.
  // Сохранение происходит только при Enter. Используется в сценарии переименования (BaseListElementLite).
  @Prop({ default: false })
  revertOnBlur!: boolean;
  // Если true — длинные слова/ссылки в тексте переносятся по буквам, а не только по
  // пробелам. По умолчанию выключено, поведение прежнее. Нужно для ячейки комментария.
  @Prop({ default: false })
  breakAnywhere!: boolean;

  errorCircleSvg = errorCircleSvg;

  $el!: HTMLElement;
  editable_field: HTMLElement | null = null;
  editMode: boolean = false;
  editingNow: boolean = false;
  // Флаг намерения коммита при ближайшем blur. Поднимается только при Enter.
  // Любой другой источник blur (Esc, клик мимо) откатит значение, если revertOnBlur=true.
  commitOnNextBlur: boolean = false;

  realTimeText: string = "";
  tooltipStyle: Record<string, string> = {};

  mounted() {
    this.editable_field = this.$refs.editable_field as HTMLElement;
    this.realTimeText = this.modelValue ?? "";
    this.renderHighlight();
  }

  beforeUnmount() {
    window.removeEventListener('scroll', this.onScrollUpdateTooltip, true);
  }

  get isContentEditable(): boolean {
    if (this.disabledField) {
      return false;
    }
    return this.editMode || this.editOnClick;
  }

  get isEmptyWarning(): boolean {
    return this.underlineEdit && this.editingNow && !this.realTimeText.trim();
  }

  get isValidationWarning(): boolean {
    return this.isEmptyWarning || (this.underlineEdit && this.editingNow && this.externalWarning);
  }

  get validationWarningText(): string {
    if (this.isEmptyWarning) return this.$t('field_cannot_be_empty');
    return this.externalWarningText || '';
  }

  get showClearIcon() {
    if (!this.modelValue || this.disabledField) return false;

    return (
      (this.inputType === "number" &&
        parseFloat(("" + (this.modelValue || "0")).replaceAll(",", ".")) !=
          0) ||
      ((this.editOnClick || this.editingNow) &&
        this.inputType === "text" &&
        this.modelValue)
    );
  }

  onEnter(event: KeyboardEvent) {
    if (this.inputType == "number" || !this.allowLineBreak) {
      if (this.underlineEdit && this.isValidationWarning) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      event.stopPropagation();
    }
  }

  apply() {
    if (this.disabledField) {
      return;
    }

    if (this.editable_field) {
      let text = this.editable_field.innerText || "";

      if (this.underlineEdit && this.isValidationWarning) {
        this.editable_field.textContent = this.modelValue || "";
        this.realTimeText = this.modelValue || "";
        this.editMode = false;
        this.editingNow = false;
        this.commitOnNextBlur = false;
        this.renderHighlight();
        return;
      }

      // Сбрасываем намерение коммита сразу — независимо от ветки ниже,
      // следующий blur уже не должен считаться коммитом.
      const isCommit = this.commitOnNextBlur;
      this.commitOnNextBlur = false;

      // revertOnBlur: blur/Esc возвращают исходное значение без emit'а.
      // Save выполняется только при коммите по Enter (выставляет commitOnNextBlur=true).
      if (this.revertOnBlur && !isCommit) {
        this.editable_field.textContent = this.modelValue || "";
        this.realTimeText = this.modelValue || "";
        this.editMode = false;
        this.editingNow = false;
        this.renderHighlight();
        this.$emit("liveValue", this.realTimeText);
        return;
      }

      this.validateInput(text);
      this.$emit("update:modelValue", (this.inputType == 'number' && !text) ? '0' : text.replaceAll(",", "."));
    }
    this.editMode = false;
    this.editingNow = false;
    this.renderHighlight();
  }

  onBlur() {
    this.apply();
    this.$emit("blur", new Event("blur"));
  }

  validateInput(text: string) {
    if (this.inputType === "number") {
      // Приводим значение к числу и обратно к строке, чтобы убрать лишние нули или невалидные символы
      let numValue =
        "" + text ? parseFloat(("" + text).replaceAll(",", ".")) : 0;
      if (isNaN(numValue)) {
        text = "0";
      } else {
        if (
          this.maxNumberValue !== undefined &&
          numValue > this.maxNumberValue
        ) {
          numValue = this.maxNumberValue;
        }
        if (
          this.minNumberValue !== undefined &&
          numValue < this.minNumberValue
        ) {
          numValue = this.minNumberValue;
        }
        text = numValue.toString();
      }

      this.editable_field && (this.editable_field.textContent = text);
    } else {
      this.editable_field && (this.editable_field.textContent = text);
    }
  }

  handleKeydownUnified(event: KeyboardEvent) {
    if (this.disabledField) {
      return;
    }

    // 1. Сначала применяем логику фильтрации (из onKeydownFilter)
    if (this.inputType === "number") {
      // ... (Ваша логика фильтрации из предыдущего ответа) ...
      // Если ввод невалидный, вызываем preventDefault() и возвращаемся
      if (this.isKeyInvalidForNumber(event)) {
        event.preventDefault();
        // Мы не эмитируем событие keydown наверх, если оно было заблокировано фильтром
        return;
      }
    }

    if (this.editable_field && parseFloat(this.editable_field.innerText) == 0) {
      this.editable_field.innerText = "";
    }

    if ((!this.allowLineBreak || this.inputType == 'number') && event.key == "Enter") {
      // Enter — это намеренный коммит. Поднимаем флаг, чтобы apply() сохранил значение.
      this.commitOnNextBlur = true;
      event.preventDefault();
      event.stopPropagation();
      this.editable_field && this.editable_field.blur();
    } else if (event.key == "Escape") {
      // Esc — отмена редактирования. Флаг коммита не поднимаем, при revertOnBlur произойдёт откат.
      this.commitOnNextBlur = false;
      event.preventDefault();
      event.stopPropagation();
      this.editable_field && this.editable_field.blur();
    }

    // Используем setTimeout, чтобы получить позицию ПОСЛЕ того, как символ напечатается
    setTimeout(() => this.emitCursor(), 0);

    // 2. Если фильтр пропущен (ввод валидный), эмитируем событие наверх (из onKeydown)
    // Это позволяет родительскому компоненту ловить @keydown.esc и т.д.
    this.$emit("keydown", event);
  }

  onInput() {
    this.realTimeText = this.editable_field
      ? this.editable_field.innerText || ""
      : "";
    this.$emit("liveValue", this.realTimeText);
    this.emitCursor(); // Добавлено
  }

  // Добавьте обработчик клика в темплейт (см. ниже), чтобы ловить перемещение курсора мышкой
  onClick() {
    if (this.disabledField || !this.editOnClick) { return }

    if (this.editable_field && this.highlightTokens.length) {
      this.editable_field.textContent = this.editable_field.innerText || '';
    }
    this.editingNow = true;
    if (this.allowEmptyNumber && this.editable_field && parseFloat(this.editable_field.innerText) == 0) {
      this.editable_field.innerText = "";
    }
    this.emitCursor();
  }

  onClear() {
    if (!this.editable_field) { return }

    // В режиме внешней валидационной ошибки (дубликат имени) "сброс" должен
    // вернуть исходное значение и завершить редактирование без сохранения.
    if (this.underlineEdit && this.externalWarning) {
      const original = this.modelValue || "";
      this.editable_field.textContent = original;
      this.realTimeText = original;
      this.$emit("liveValue", this.realTimeText);
      this.$nextTick(() => {
        this.editable_field && this.editable_field.blur();
      });
      return;
    }

    const cleared = this.inputType === "number" ? "0" : "";
    this.editable_field.textContent = cleared;
    this.realTimeText = cleared;

    // Остаёмся в режиме редактирования, не вызываем onBlur/apply
    this.editingNow = true;
    this.editMode = true;

    const that = this;
    this.$nextTick(() => {
      if (that.editable_field) {
        if (that.allowEmptyNumber && parseFloat(that.editable_field.innerText) == 0) {
          that.editable_field.innerText = "";
        }
        that.editable_field.focus();
        that.emitCursor();
      }
    });
  }

  // Вспомогательный метод для чистоты кода (логика из onKeydownFilter)
  private isKeyInvalidForNumber(event: KeyboardEvent): boolean {
    // Разрешаем специальные клавиши и Ctrl/Cmd операции
    if (
      [
        "Backspace",
        "Delete",
        "Tab",
        "Escape",
        "Enter",
        "ArrowLeft",
        "ArrowRight",
      ].includes(event.key) ||
      ((event.ctrlKey === true || event.metaKey === true) &&
        ["a", "c", "v", "x"].includes(event.key.toLowerCase()))
    ) {
      return false; // Клавиша валидна
    }

    // Разрешаем ввод цифр (0-9)
    if (/^\d$/.test(event.key)) {
      return false; // Клавиша валидна
    }

    // Разрешаем десятичный разделитель
    if (this.editable_field && ["."/*, ","*/].includes(event.key)) {
      if (
        !this.editable_field.innerText.includes(".")
      ) {
        return false; // Клавиша валидна (первый разделитель)
      }
    }

    // Все остальное невалидно для числового ввода
    return true;
  }

  startEdit() {
    if (this.disabledField) {
      return;
    }

    if (this.editable_field) {
      if (this.highlightTokens.length) {
        this.editable_field.textContent = this.editable_field.innerText || '';
      }

      this.editMode = true;
      this.editingNow = true;
      const that = this;

      this.$nextTick(() => {
        if (that.editable_field) {
          that.editable_field.focus();
          // if (pos_start) {
          // @ts-ignore
          that.editable_field.selectionStart = 0;
          const selection = window.getSelection();
          const range = document.createRange();
          range.selectNodeContents(that.editable_field);
          selection?.removeAllRanges();
          selection?.addRange(range);
          // }
          that.emitCursor();
        }
      });
    }
  }

  @Watch("modelValue")
  @Watch("maxNumberValue")
  @Watch("minNumberValue")
  onValueChanged(newVal: string) {
    // не трогать, если пользователь сейчас печатает
    if (document.activeElement === this.editable_field) return;

    const that = this;
    this.$nextTick(() => {
      that.validateInput(this.modelValue || "");

      if (newVal == (this.modelValue || "")) {
        that.apply();
        this.emitCursor();
      }
      that.renderHighlight();
    });
  }

  @Watch("highlightTokens")
  onHighlightTokensChanged() {
    if (!this.editingNow) {
      this.$nextTick(() => this.renderHighlight());
    }
  }

  @Watch("isValidationWarning")
  onValidationWarningChanged(val: boolean) {
    if (val) {
      this.$nextTick(() => {
        this.updateTooltipPosition();
        window.addEventListener('scroll', this.onScrollUpdateTooltip, true);
      });
    } else {
      window.removeEventListener('scroll', this.onScrollUpdateTooltip, true);
    }
  }

  private onScrollUpdateTooltip = () => {
    this.updateTooltipPosition();
  };

  private updateTooltipPosition() {
    const icon = this.$el?.querySelector('.native-input__error-icon') as HTMLElement;
    if (!icon) return;
    const rect = icon.getBoundingClientRect();
    this.tooltipStyle = {
      position: 'fixed',
      top: `${rect.top - 8}px`,
      left: `${rect.left}px`,
      transform: 'translateY(-100%)',
    };
  }

  private renderHighlight() {
    if (!this.editable_field) return;
    const text = this.modelValue ?? '';
    if (this.editingNow || !this.highlightTokens || !this.highlightTokens.length) {
      this.editable_field.textContent = text;
      return;
    }
    const spans = buildHighlightSpans(text, this.highlightTokens);
    const hasHighlight = spans.some(s => !!s.style);
    if (!hasHighlight) {
      this.editable_field.textContent = text;
      return;
    }
    this.editable_field.innerHTML = spans.map(s => {
      const escaped = this.escapeHtml(s.text);
      return s.style ? `<span style="${s.style}">${escaped}</span>` : escaped;
    }).join('');
  }

  private escapeHtml(text: string): string {
    return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  // Метод для вычисления позиции каретки
  private getCaretPosition(): number {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const preCaretRange = range.cloneRange();
      preCaretRange.selectNodeContents(this.editable_field!);
      preCaretRange.setEnd(range.endContainer, range.endOffset);
      return preCaretRange.toString().length;
    }
    return 0;
  }

  private emitCursor() {
    const pos = this.getCaretPosition();
    this.$emit("update:caretPosition", pos);
  }
}
</script>

<style lang="scss">
.native-input {
  outline: none;
  padding: 2px 5px;
  border-radius: 4px;
  border: 1px solid transparent;
  align-items: center;
  min-height: 26px;
  height: min-content;

  &-border {
    @extend .native-input;
    border: 1px solid lightgrey;

    &.is-editable:hover {
      border-color: $accent !important;
    }
  }

  // Стили применятся только если есть класс .is-editable
  &.is-editable:hover {
    border-color: $accent !important;
  }
}

.inner-editable {
  outline: none;
  // margin-top: 2px;
  width: -moz-available;
  width: -webkit-fill-available;
  white-space: pre-wrap;
  min-width: 20px;
  position: relative;
  top: 0;
}

/* Опционально: перенос длинных слов/ссылок по буквам (breakAnywhere). */
.native-input--break-anywhere .inner-editable {
  overflow-wrap: anywhere;
  word-break: break-word;
}

.native-input:focus-within {
  border-color: $accent;
}

.native-input--underline {
  border: none !important;
  border-radius: 0 !important;
  padding: 0 !important;
  min-height: auto !important;

  &.is-editable:hover {
    border-color: transparent !important;
  }

  &:focus-within .inner-editable {
    text-decoration: underline;
    text-decoration-color: #3F884F;
    text-underline-offset: 2px;
  }

  &.native-input--empty-warning:focus-within .inner-editable {
    text-decoration-color: #F53;
  }
}

.native-input--underline.native-input--editing .inner-editable {
  top: 1.5px;
}

.native-input__error-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  margin-left: 2px;
}

.native-input__error-icon {
  width: 16px;
  height: 16px;
  aspect-ratio: 1/1;
  flex-shrink: 0;
}

.native-input__error-tooltip {
  border-radius: 8px;
  background: var(--color-tooltip, rgba(92, 92, 92, 0.90));
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.20);
  padding: 2px 8px;
  color: var(--Text-color-text-btn, #FFF);
  font-family: Roboto, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 10000;
}

/* По умолчанию иконка прозрачна (когда v-if ее отрисовал) */
.target-icon {
  opacity: 0;
  transition: opacity 0.2s ease; /* Плавность появления */
  pointer-events: none; /* Чтобы нельзя было кликнуть на невидимую */
}

.native-input__clear-icon {
  margin-left: 8px;
}

.native-input__error-wrap + .native-input__clear-wrap .native-input__clear-icon {
  margin-left: 2px;
}

/* При наведении на родителя — показываем */
.parent-box:hover .target-icon {
  opacity: 1;
  pointer-events: auto; /* Возвращаем кликабельность */
}

/* В режиме редактирования кнопка очистки видна всегда, без наведения. */
.native-input--editing .native-input__clear-icon {
  opacity: 1;
  pointer-events: auto;
}

</style>
